import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchProductById } from '../../../slice/product/productSlice';
import { addToCart, fetchCart } from '../../../slice/cart/cartSlice';
import { fetchCategoryDropdown } from '../../../slice/category/categorySlice';
import Header from '../Header';
import Footer from '../components/Footer';
import Popuplogin from '../components/Popuplogin';
import ImageCropModal from './ImageCropModal';
import FrameCompositor from './Framecompositor';
import "../components/css/Productcustomizer.css";

/* ─── helpers ─────────────────────────────────────────────────── */
const BASE = process.env.REACT_APP_API_URL;
const imgUrl = (path) => `${BASE}/uploads/${path}`;

const getVariantOri = (product, colorCode, orientation) => {
  if (!product?.colorVariants || !colorCode) return null;
  const colorObj = product.colors?.find((c) => c.code === colorCode);
  if (!colorObj) return null;
  const variant = product.colorVariants.find((v) => {
    const vid = v.colorId?._id || v.colorId;
    return vid === colorObj._id;
  });
  return variant?.[orientation] || null;
};

/* ─── helper: best default orientation for a color ── */
const getBestOrientation = (product, colorCode) => {
  if (!product || !colorCode) return 'portrait';
  const colorObj = product.colors?.find((c) => c.code === colorCode);
  const variant = product.colorVariants?.find((v) => {
    const vid = v.colorId?._id || v.colorId;
    return vid === colorObj?._id;
  });
  const portraitHasContent = !!(variant?.portrait?.previewImage || variant?.portrait?.images?.length);
  const landscapeHasContent = !!(variant?.landscape?.previewImage || variant?.landscape?.images?.length);
  return portraitHasContent ? 'portrait' : landscapeHasContent ? 'landscape' : 'portrait';
};

/* ─── component ────────────────────────────────────────────────── */
const ProductCustomizer = () => {
  const { productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { singleProduct, loading: productsLoading } = useSelector((s) => s.product);
  const { dropdownCategories = [], loading: catLoading } = useSelector((s) => s.category);
  const { user, token } = useSelector((s) => s.auth);
  const { loading: cartLoading } = useSelector((s) => s.cart);

  const compositorRef = useRef(null);
  const previewWrapRef = useRef(null);

  /* ── core state ── */
  const [product, setProduct] = useState(null);
  const [fetchError, setFetchError] = useState(false);

  /* ── selection state ── */
  const [selectedColor, setSelectedColor] = useState('');
  const [orientation, setOrientation] = useState('portrait');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedSizeEntry, setSelectedSizeEntry] = useState(null);

  /* ── user inputs ── */
  const [textInputs, setTextInputs] = useState([]);
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  /* ── ui state ── */
  const [validationErrors, setValidationErrors] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);

  /* ── compositor remount key: changes when color/orientation change so
       FrameCompositor fully reinitialises (clears stale canvas) ── */
  const [compositorKey, setCompositorKey] = useState(0);

  /* ── crop state ── */
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropTargetIndex, setCropTargetIndex] = useState(null);

  /* ── scroll to top ── */
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [productId]);

  /* ── fetch ── */
  useEffect(() => { dispatch(fetchCategoryDropdown()); }, [dispatch]);

  useEffect(() => {
    if (productId) {
      dispatch(fetchProductById(productId)).unwrap().catch(() => setFetchError(true));
    }
  }, [dispatch, productId]);

  /* ── hydrate when product loads ── */
  useEffect(() => {
    if (singleProduct && singleProduct._id === productId) {
      setProduct(singleProduct);

      const firstColor = singleProduct.colors?.[0];
      if (firstColor) {
        setSelectedColor(firstColor.code);
        // Auto-detect best orientation for first color
        setOrientation(getBestOrientation(singleProduct, firstColor.code));
      }

      const firstSize = singleProduct.sizes?.[0];
      if (firstSize) {
        setSelectedSize(firstSize.size?.name || firstSize.name || '');
        setSelectedSizeEntry(firstSize);
      }

      setTextInputs(Array(singleProduct.textCount || 0).fill(''));
      setImages(Array(singleProduct.imageCount || 0).fill(''));
      setImageFiles(Array(singleProduct.imageCount || 0).fill(null));
      setSliderIndex(0);
      setCompositorKey((k) => k + 1);
    }
  }, [singleProduct, productId]);

  /* ── reset images when color/orientation changes ── */
  useEffect(() => {
    if (!product) return;
    setImages(Array(product.imageCount || 0).fill(''));
    setImageFiles(Array(product.imageCount || 0).fill(null));
    setSliderIndex(0);
    setValidationErrors([]);
    // Force compositor to remount so it re-loads the new background image cleanly
    setCompositorKey((k) => k + 1);
  }, [selectedColor, orientation, product]);

  /* ── derived: active variant orientation data ── */
  const activeOri = getVariantOri(product, selectedColor, orientation);
  const previewImageUrl = activeOri?.previewImage ? imgUrl(activeOri.previewImage) : null;
  const photoZones = activeOri?.photoZones || [];
  const textZones = activeOri?.textZones || [];
  const hasZones = photoZones.length > 0;
  const productImgUrls = (activeOri?.images || []).map((img) => imgUrl(img));

  /* ── unified slides ── */
  const allSlides = hasZones
    ? [
        { type: 'compositor' },
        ...productImgUrls.map((src) => ({ src, type: 'product' })),
      ]
    : [
        ...(previewImageUrl ? [{ src: previewImageUrl, type: 'preview' }] : []),
        ...productImgUrls.map((src) => ({ src, type: 'product' })),
      ];

  const currentSlide = allSlides[sliderIndex] || allSlides[0];

  const goNext = () => setSliderIndex((p) => (p + 1) % allSlides.length);
  const goPrev = () => setSliderIndex((p) => (p - 1 + allSlides.length) % allSlides.length);

  /* ── handlers ── */
  const handleSizeChange = (sizeName) => {
    setSelectedSize(sizeName);
    const entry = product?.sizes?.find((s) => (s.size?.name || s.name) === sizeName);
    setSelectedSizeEntry(entry || null);
    setValidationErrors([]);
  };

  const handleColorChange = (colorCode) => {
    setSelectedColor(colorCode);
    // Auto-select best orientation for the newly selected color
    setOrientation(getBestOrientation(product, colorCode));
  };

  const handleOrientationChange = (ori) => {
    setOrientation(ori);
  };

  const handleTextChange = (index, value) => {
    const updated = [...textInputs];
    updated[index] = value;
    setTextInputs(updated);
    setValidationErrors([]);
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropImageSrc(ev.target.result);
      setCropTargetIndex(index);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const triggerZoneUpload = (index) => {
    document.getElementById(`zone-upload-${index}`)?.click();
  };

  const handleCropDone = ({ file, dataUrl }) => {
    const idx = cropTargetIndex;

    setImages((prev) => {
      const n = [...prev];
      n[idx] = dataUrl;
      return n;
    });
    setImageFiles((prev) => {
      const n = [...prev];
      n[idx] = file;
      return n;
    });

    setCropModalOpen(false);
    setCropImageSrc(null);
    setCropTargetIndex(null);
    setValidationErrors([]);

    // Jump to compositor slide so the user sees their image immediately
    setSliderIndex(0);
  };

  /* ── derived price ── */
  const activeMrp = selectedSizeEntry?.mrp ?? product?.mrp ?? 0;
  const activeSellingPrice = selectedSizeEntry?.sellingPrice ?? product?.sellingPrice ?? 0;
  const activeDiscount = activeMrp > activeSellingPrice
    ? Math.round(((activeMrp - activeSellingPrice) / activeMrp) * 100)
    : 0;

  const uploadedCount = imageFiles.filter(Boolean).length;

  /* ── validation ── */
  const validateForm = () => {
    const errors = [];
    if (product?.imageCount > 0 && uploadedCount < product.imageCount)
      errors.push(`Please upload ${product.imageCount} image(s)`);
    if (product?.textCount > 0 && textInputs.some((t) => !t.trim()))
      errors.push('Please fill in all text fields');
    if (!selectedSize) errors.push('Please select a frame size');
    if (!selectedColor) errors.push('Please select a frame color');
    setValidationErrors(errors);
    return errors.length === 0;
  };

  /* ── add to cart ── */
  const handleAddToCart = async () => {
    if (!token || !user) {
      setShowLoginModal(true);
      return;
    }

    if (!validateForm()) return;

    const selectedColorObj = product?.colors?.find((c) => c.code === selectedColor);

    const formData = new FormData();
    formData.append('productId', product._id);
    formData.append('frameSize', selectedSize);
    formData.append('frameColor', selectedColorObj?.name || '');
    formData.append('productColor', selectedColorObj?.code || '');
    formData.append('orientation', orientation);
    formData.append('quantity', 1);

    textInputs.forEach((t) => formData.append('texts[]', t));
    imageFiles.forEach((file) => {
      if (file) formData.append('images', file);
    });

    dispatch(addToCart(formData))
      .unwrap()
      .then(() => {
        dispatch(fetchCart());
        toast.success('Item added to cart 🛒');
        const offcanvasEl = document.getElementById('offcanvasRight');
        if (offcanvasEl && window.bootstrap) {
          window.bootstrap.Offcanvas.getOrCreateInstance(offcanvasEl).show();
        }
      })
      .catch(() => toast.error('Failed to add item'));
  };

  /* ── zone style helpers ── */
  const getZoneStyle = (zone) => {
    if (!zone?.quad) return {};
    const { TL, TR, BR, BL } = zone.quad;
    const minX = Math.min(TL.x, TR.x, BR.x, BL.x);
    const minY = Math.min(TL.y, TR.y, BR.y, BL.y);
    const maxX = Math.max(TL.x, TR.x, BR.x, BL.x);
    const maxY = Math.max(TL.y, TR.y, BR.y, BL.y);
    const rx = (p) => `${((p - minX) / (maxX - minX)) * 100}%`;
    const ry = (p) => `${((p - minY) / (maxY - minY)) * 100}%`;
    return {
      left: `${minX}%`, top: `${minY}%`,
      width: `${maxX - minX}%`, height: `${maxY - minY}%`,
      clipPath: `polygon(${rx(TL.x)} ${ry(TL.y)},${rx(TR.x)} ${ry(TR.y)},${rx(BR.x)} ${ry(BR.y)},${rx(BL.x)} ${ry(BL.y)})`,
    };
  };

  const getTextZoneStyle = (zone) => {
    if (!zone?.quad) return {};
    const { TL, TR, BR, BL } = zone.quad;
    const minX = Math.min(TL.x, TR.x, BR.x, BL.x);
    const minY = Math.min(TL.y, TR.y, BR.y, BL.y);
    const maxX = Math.max(TL.x, TR.x, BR.x, BL.x);
    const maxY = Math.max(TL.y, TR.y, BR.y, BL.y);
    return {
      position: 'absolute',
      left: `${minX}%`,
      top: `${minY}%`,
      width: `${maxX - minX}%`,
      height: `${maxY - minY}%`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      overflow: 'hidden',
    };
  };

const handleLoginSuccess = () => {
  setShowLoginModal(false);
  setTimeout(() => {
    handleAddToCart();
  }, 300);
};
  /* ── loading / error ── */
  if (productsLoading || (!product && !fetchError)) {
    return (
      <div className="page-wraper">
        <Header dropdownCategories={dropdownCategories} loading={catLoading} />
        <div className="pc-loading-wrap">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (fetchError || !product) {
    return (
      <div className="page-wraper">
        <Header dropdownCategories={dropdownCategories} loading={catLoading} />
        <div className="pc-error-wrap">
          <h4 className="pc-error-title">Product not found</h4>
          <p className="pc-error-subtitle">This product may have been removed or the link is invalid.</p>
          <button className="pc-btn-go-back" onClick={() => navigate(-1)}>← Go Back</button>
        </div>
        <Footer />
      </div>
    );
  }

  /* ── render ── */
  return (
    <div className="page-wraper">
      <Header dropdownCategories={dropdownCategories} loading={catLoading} />

      <div className="page-content pc-page-bg">

        {/* Breadcrumb */}
        <div className="pc-breadcrumb-bar">
          <div className="container">
            <nav className="pc-breadcrumb-nav">
              <span className="pc-breadcrumb-link" onClick={() => navigate('/')}>Home</span>
              <span>›</span>
              <span className="pc-breadcrumb-link" onClick={() => navigate(-1)}>Products</span>
              <span>›</span>
              <span className="pc-breadcrumb-current">{product.name}</span>
            </nav>
          </div>
        </div>

        <div className="container pc-container">
          <div className="pc-grid">

            {/* ═══════════ LEFT: Preview Panel ═══════════ */}
            <div className="pc-left-col">

              {/* Size badge */}
              <div className="pc-preview-meta">
                {selectedSize && (
                  <span className="pc-size-badge">📐 {selectedSize} inches</span>
                )}
                <span className="pc-upload-status">
                  {uploadedCount}/{product.imageCount || 0} photos uploaded
                </span>
              </div>

              {/* Main preview card */}
              <div className="pc-card">

                {/* Color variant tabs */}
                {product.colors?.length > 0 && (
                  <div className="pc-color-tabs">
                    {product.colors.map((color) => (
                      <button
                        key={color._id}
                        className={`pc-color-tab${selectedColor === color.code ? ' active' : ''}`}
                        onClick={() => handleColorChange(color.code)}
                      >
                        <span className="pc-color-tab-dot" style={{ background: color.code }} />
                        {color.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Orientation tabs — only show if BOTH landscape AND portrait have content */}
                {(() => {
                  const landscapeVariant = getVariantOri(product, selectedColor, 'landscape');
                  const portraitVariant = getVariantOri(product, selectedColor, 'portrait');
                  const landscapeHasContent = !!(landscapeVariant?.previewImage || landscapeVariant?.images?.length);
                  const portraitHasContent = !!(portraitVariant?.previewImage || portraitVariant?.images?.length);
                  const bothHaveContent = landscapeHasContent && portraitHasContent;

                  if (!bothHaveContent) return null;

                  return (
                    <div className="pc-ori-tabs">
                      {['portrait', 'landscape'].map((ori) => (
                        <button
                          key={ori}
                          className={`pc-ori-tab${orientation === ori ? ' active' : ''}`}
                          onClick={() => handleOrientationChange(ori)}
                        >
                          {ori === 'landscape' ? '🖼️' : '📱'} {ori.charAt(0).toUpperCase() + ori.slice(1)}
                          <span className="pc-ori-check">✓</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}

                {/* Hidden file inputs (one per zone) */}
                {hasZones && photoZones.map((_, idx) => (
                  <input
                    key={idx}
                    type="file"
                    id={`zone-upload-${idx}`}
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleImageUpload(idx, e)}
                  />
                ))}

                <div className="pc-card-body">

                  {/* ══ UNIFIED SLIDER ══ */}
                  <div className="pc-slider-wrap" ref={previewWrapRef}>

                    {/* Compositor slide */}
                    {currentSlide?.type === 'compositor' && (
                      <div className="pc-frame-wrap">
                        <div className="pc-frame-inner" style={{ position: 'relative' }}>

                          <FrameCompositor
                            key={compositorKey}
                            ref={compositorRef}
                            previewImageUrl={previewImageUrl}
                            photoZones={photoZones}
                            userPhotos={images}
                            fit="cover"
                            outputWidth={1200}
                            style={{ border: 'none', boxShadow: 'none', display: 'block', width: '100%' }}
                          />

                          {/* Photo zone overlay buttons */}
                          {photoZones.map((zone, idx) => {
                            const uploaded = !!images[idx];
                            const zoneStyle = getZoneStyle(zone);
                            return (
                              <div
                                key={idx}
                                onClick={() => triggerZoneUpload(idx)}
                                className={`pc-zone-overlay${uploaded ? ' uploaded' : ''}`}
                                style={{ position: 'absolute', ...zoneStyle }}
                                onMouseEnter={(e) => {
                                  if (uploaded) {
                                    e.currentTarget.style.background = 'rgba(0,0,0,0.18)';
                                    const lbl = e.currentTarget.querySelector('.pc-zone-lbl');
                                    if (lbl) lbl.style.opacity = '1';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (uploaded) {
                                    e.currentTarget.style.background = 'transparent';
                                    const lbl = e.currentTarget.querySelector('.pc-zone-lbl');
                                    if (lbl) lbl.style.opacity = '0';
                                  }
                                }}
                              >
                                {!uploaded ? (
                                  <div className="pc-zone-upload-pill">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                      <polyline points="17 8 12 3 7 8"/>
                                      <line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    Upload {idx + 1}
                                  </div>
                                ) : (
                                  <div className="pc-zone-lbl" style={{ opacity: 0 }}>
                                    ✏️ Change {idx + 1}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Text zone overlays */}
                          {textZones.map((zone, idx) => {
                            const text = textInputs[idx] || '';
                            return (
                              <div
                                key={idx}
                                style={{
                                  ...getTextZoneStyle(zone),
                                  border: text ? 'none' : '2px dashed rgba(255,255,255,0.75)',
                                  borderRadius: '4px',
                                  boxSizing: 'border-box',
                                }}
                              >
                                {text ? (
                                  <span
                                    style={{
                                      color: zone.color || '#fff',
                                      fontSize: zone.fontSize ? `${zone.fontSize}px` : '14px',
                                      fontFamily: zone.fontFamily || 'inherit',
                                      fontWeight: zone.fontWeight || 'bold',
                                      textAlign: 'center',
                                      textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      maxWidth: '100%',
                                      padding: '0 4px',
                                    }}
                                  >
                                    {text}
                                  </span>
                                ) : null}
                              </div>
                            );
                          })}

                          {/* Fallback text (when no textZones defined) */}
                          {(!textZones || textZones.length === 0) && textInputs.some(Boolean) && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '8%',
                                left: 0,
                                right: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                                pointerEvents: 'none',
                                padding: '0 12px',
                              }}
                            >
                              {textInputs.filter(Boolean).map((t, i) => (
                                <span
                                  key={i}
                                  style={{
                                    color: '#fff',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    textShadow: '0 1px 4px rgba(0,0,0,0.8)',
                                    background: 'rgba(0,0,0,0.25)',
                                    padding: '2px 10px',
                                    borderRadius: '4px',
                                    maxWidth: '90%',
                                    textAlign: 'center',
                                  }}
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Product image / preview image slide */}
                    {(currentSlide?.type === 'product' || currentSlide?.type === 'preview') && (
                      <div className="pc-slider-main">
                        <img
                          src={currentSlide.src}
                          alt={`Product view ${sliderIndex + 1}`}
                          className="pc-slider-img"
                        />
                        {currentSlide.type === 'preview' && (
                          <span className="pc-slider-preview-badge">Preview</span>
                        )}
                      </div>
                    )}

                    {/* No image fallback */}
                    {allSlides.length === 0 && (
                      <div className="pc-no-image-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21 15 16 10 5 21"/>
                        </svg>
                        <p>No image available</p>
                      </div>
                    )}

                    {/* Arrow navigation */}
                    {allSlides.length > 1 && (
                      <>
                        <button className="pc-slider-arrow pc-slider-arrow-left" onClick={goPrev} aria-label="Previous">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <polyline points="15 18 9 12 15 6"/>
                          </svg>
                        </button>
                        <button className="pc-slider-arrow pc-slider-arrow-right" onClick={goNext} aria-label="Next">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </button>
                        <div className="pc-slider-dots">
                          {allSlides.map((_, i) => (
                            <button
                              key={i}
                              className={`pc-slider-dot${i === sliderIndex ? ' active' : ''}`}
                              onClick={() => setSliderIndex(i)}
                              aria-label={`Slide ${i + 1}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Upload hint */}
                  {hasZones && uploadedCount === 0 && currentSlide?.type === 'compositor' && (
                    <p className="pc-zone-hint">Tap the upload button on the frame to add your photo ↑</p>
                  )}
                </div>

                {/* Thumbnail strip */}
                {allSlides.length > 1 && (
                  <div className="pc-thumb-strip">
                    {allSlides.map((slide, i) => {
                      const isActive = i === sliderIndex;
                      const thumbSrc = slide.type === 'compositor' ? previewImageUrl : slide.src;
                      return (
                        <button
                          key={i}
                          className={`pc-thumb${isActive ? ' active' : ''}`}
                          onClick={() => setSliderIndex(i)}
                          aria-label={`Thumbnail ${i + 1}`}
                        >
                          {thumbSrc ? (
                            <>
                              <img src={thumbSrc} alt={`Thumb ${i + 1}`} />
                              {slide.type === 'compositor' && (
                                <span className="pc-thumb-preview-label">Preview</span>
                              )}
                            </>
                          ) : (
                            <span style={{ fontSize: 10, color: '#888' }}>🖼</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ═══════════ RIGHT: Options Panel ═══════════ */}
            <div className="pc-right-sticky">

              {/* Product info */}
              <div className="pc-card">
                <div className="pc-card-body">
                  <h1 className="pc-product-title">{product.name}</h1>
                  <div className="pc-badges-row">
                    {product.label?.name && (
                      <span className="pc-label-badge">{product.label.name}</span>
                    )}
                  </div>
                  <div className="pc-price-row">
                    <span className="pc-selling-price">₹{activeSellingPrice.toLocaleString('en-IN')}</span>
                    <span className="pc-mrp">₹{activeMrp.toLocaleString('en-IN')}</span>
                    {activeDiscount > 0 && (
                      <span className="pc-discount-badge">{activeDiscount}% OFF</span>
                    )}
                  </div>
                  {product.description && (
                    <p className="pc-description">{product.description}</p>
                  )}
                </div>
              </div>

              {/* Frame size */}
              {product.sizes?.length > 0 && (
                <div className="pc-card-sm">
                  <div className="pc-card-header-row">
                    <span className="pc-section-label">Frame size</span>
                    {selectedSize && <span className="pc-section-label-accent">{selectedSize} selected</span>}
                  </div>
                  <div className="pc-size-options">
                    {product.sizes.map((s) => {
                      const name = s.size?.name || s.name || '';
                      return (
                        <button
                          key={name}
                          className={`pc-size-btn${selectedSize === name ? ' active' : ''}`}
                          onClick={() => handleSizeChange(name)}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Frame color */}
              {product.colors?.length > 0 && (
                <div className="pc-card-sm">
                  <div className="pc-card-header-row">
                    <span className="pc-section-label">Frame color</span>
                    {selectedColor && (
                      <span className="pc-section-label-accent">
                        {product.colors.find((c) => c.code === selectedColor)?.name || ''}
                      </span>
                    )}
                  </div>
                  <div className="pc-color-options">
                    {product.colors.map((color) => (
                      <button
                        key={color._id}
                        title={color.name}
                        className={`pc-color-swatch${selectedColor === color.code ? ' active' : ''}`}
                        style={{ background: color.code }}
                        onClick={() => handleColorChange(color.code)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Upload photos */}
              {product.imageCount > 0 && (
                <div className="pc-card-sm">
                  <div className="pc-card-header-row">
                    <span className="pc-section-label">
                      Upload photos <span className="pc-required-star">*</span>
                    </span>
                    <span className="pc-photo-count-badge">{uploadedCount}/{product.imageCount} uploaded</span>
                  </div>

                  {/* Progress bar */}
                  <div className="pc-progress-row">
                    <div className="pc-progress-bar">
                      <div
                        className="pc-progress-fill"
                        style={{ width: `${Math.round((uploadedCount / (product.imageCount || 1)) * 100)}%` }}
                      />
                    </div>
                  </div>

                  {hasZones ? (
                    <div className="pc-zone-upload-pills">
                      {images.map((img, idx) => (
                        <div
                          key={idx}
                          className={`pc-zone-upload-btn${img ? ' uploaded' : ''}`}
                          onClick={() => triggerZoneUpload(idx)}
                        >
                          {img ? (
                            <>
                              <img src={img} alt="" className="pc-zone-thumb" />
                              ✓ Photo {idx + 1}
                            </>
                          ) : (
                            <>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                              </svg>
                              Photo {idx + 1}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="pc-upload-grid">
                      {images.map((img, idx) => (
                        <label key={idx} htmlFor={`upload-${idx}`} className="pc-upload-label">
                          <input
                            type="file"
                            id={`upload-${idx}`}
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => handleImageUpload(idx, e)}
                          />
                          <div className={`pc-upload-box${img ? ' has-image' : ''}`}>
                            {img ? (
                              <>
                                <img src={img} alt={`upload ${idx + 1}`} className="pc-upload-img" />
                                <div className="pc-upload-overlay">
                                  <span className="pc-upload-overlay-text">Change</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                                  <circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21 15 16 10 5 21"/>
                                </svg>
                                <span className="pc-upload-placeholder-label">Photo {idx + 1}</span>
                                <span className="pc-upload-placeholder-hint">tap to upload</span>
                              </>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Text inputs */}
              {product.textCount > 0 && (
                <div className="pc-card-sm">
                  <div className="pc-card-header-row">
                    <span className="pc-section-label">
                      Personalize text <span className="pc-required-star">*</span>
                    </span>
                  </div>
                  <div className="pc-text-inputs">
                    {Array.from({ length: product.textCount }).map((_, idx) => (
                      <div key={idx} className="pc-text-field-wrap">
                        <label className="pc-text-field-label">Line {idx + 1}</label>
                        <input
                          type="text"
                          value={textInputs[idx] || ''}
                          className="pc-text-input"
                          onChange={(e) => handleTextChange(idx, e.target.value)}
                          placeholder={`Enter text line ${idx + 1}…`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Validation errors */}
              {validationErrors.length > 0 && (
                <div className="pc-errors">
                  {validationErrors.map((err, i) => (
                    <div key={i} className="pc-error-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      {err}
                    </div>
                  ))}
                </div>
              )}

              {/* CTA */}
              <div className="pc-cta-row">
                <button className="pc-btn-back" onClick={() => navigate(-1)}>← Back</button>
                <button className="pc-btn-cart" onClick={handleAddToCart} disabled={cartLoading}>
                  {cartLoading ? 'Adding…' : '🛒 Add to Cart'}
                </button>
              </div>

              {/* Trust badges */}
              <div className="pc-trust-row">
                <span className="pc-trust-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  Secure checkout
                </span>
                <span className="pc-trust-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v4h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                  Free delivery
                </span>
                <span className="pc-trust-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  Easy returns
                </span>
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />

      {/* Login modal */}

{showLoginModal && (
  <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content POP">
        <Popuplogin
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}  
        />
      </div>
    </div>
  </div>
)}
      {/* Crop modal */}
      {cropModalOpen && cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          onDone={handleCropDone}
          onCancel={() => {
            setCropModalOpen(false);
            setCropImageSrc(null);
            setCropTargetIndex(null);
          }}
          placeholders={[]}
          frameSize={selectedSize}
          orientation={orientation} 
        />
      )}
    </div>
  );
};

export default ProductCustomizer;